import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { logoutThunk } from '@/modules/auth/store/authThunk';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import {
  Briefcase,
  Users,
  Phone,
  Database,
  BarChart3,
  LogOut,
  Shield,
  Menu,
  X,
} from 'lucide-react';

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const pathname = location.pathname;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    {
      href: '/admin',
      icon: BarChart3,
      label: 'Dashboard',
      active: pathname === '/admin',
    },
    {
      href: '/admin/jobs',
      icon: Briefcase,
      label: 'Jobs',
      active: pathname === '/admin/jobs',
    },
    {
      href: '/admin/voice-agent',
      icon: Phone,
      label: 'Voice Agent',
      active: pathname === '/admin/voice-agent',
    },
    {
      href: '/admin/master-db',
      icon: Database,
      label: 'Master DB',
      active: pathname === '/admin/master-db',
    },
    {
      href: '/admin/team',
      icon: Users,
      label: 'Team',
      active: pathname === '/admin/team',
    },
  ];
  const handleLogout = async () => {
    try {
      await dispatch(logoutThunk()).unwrap();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setSidebarOpen(false);

      navigate('/', {
        replace: true,
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Header */}
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b bg-white px-4 md:hidden">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu className="h-6 w-6" />
        </Button>

        <span className="font-semibold">Admin Panel</span>

        <div className="w-9" />
      </header>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 z-50
          h-screen
          w-64
          transform
          border-r
          bg-white
          shadow-lg
          transition-transform
          duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b px-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
              <Shield className="h-5 w-5 text-white" />
            </div>

            <div>
              <h2 className="font-bold">Admin Panel</h2>
              <p className="text-xs text-muted-foreground">Dharohar Land Corp</p>
            </div>
          </div>

          {/* Close only mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="space-y-1 p-3">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                item.active
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-muted-foreground hover:bg-slate-100 hover:text-black'
              }`}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Footer */}
        {/* Footer */}
        <div className="absolute bottom-0 w-full border-t p-3">
          <Button
            type="button"
            variant="outline"
            className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="md:ml-64">
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
